import styles from './BookedFlow.module.scss';
import { ComponentProps, FC } from 'react';
import {
  Button,
  InlineNotification,
  NotificationType,
} from '@spotted-zebra-uk/ui-components';
import { useFocusOnMount } from '../../useFocusOnMount';

type ButtonVariant = NonNullable<ComponentProps<typeof Button>['variant']>;

export interface ScreenAction {
  label: string;
  onClick: () => void;
  variant: ButtonVariant;
  loading?: boolean;
}

export interface ScreenNotice {
  text: string;
  /** Offered when the remedy is something the candidate can press, such as a reload. */
  action?: { label: string; onClick: () => void };
}

interface ActionScreenProps {
  title: string;
  body?: string;
  notice?: ScreenNotice | null;
  /** Disables every action while one of them is in flight. */
  busy?: boolean;
  actions: ScreenAction[];
}

/**
 * One screen of the change-your-interview flow: a heading, what will happen,
 * and the choices. Reached by pressing a control, so it takes focus.
 */
const ActionScreen: FC<ActionScreenProps> = ({
  title,
  body,
  notice,
  busy = false,
  actions,
}) => {
  const headingRef = useFocusOnMount<HTMLHeadingElement>(true);

  return (
    <div className={styles.wrapper}>
      <div className={styles.heading}>
        <h1 className={styles.title} ref={headingRef} tabIndex={-1}>
          {title}
        </h1>
        {body && <p className={styles.subline}>{body}</p>}
      </div>
      {notice && (
        <div className={styles.notice}>
          <InlineNotification
            notificationType={NotificationType.ERROR}
            action={
              notice.action && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={notice.action.onClick}
                >
                  {notice.action.label}
                </Button>
              )
            }
          >
            <p>{notice.text}</p>
          </InlineNotification>
        </div>
      )}
      <div className={styles.actions}>
        {actions.map(action => (
          <Button
            key={action.label}
            variant={action.variant}
            size="medium"
            disabled={busy}
            loading={action.loading}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default ActionScreen;
